import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Send, CheckCircle2, Clock, XCircle, MessageSquare, RefreshCw, ExternalLink, ArrowRight, Eye } from 'lucide-react';
import { trackingReportApi } from '../lib/api';
import { FileRequestDetailsModal } from './FileRequestDetailsModal';

interface Note {
  id: string;
  work_log_id: string;
  note: string;
  created_by: string;
  created_at: string;
  author_name: string;
}

interface ReassignmentEntry {
  reassignment_note: string | null;
  created_at: string;
  from_name: string;
  to_name: string;
  progress_from_completed: number;
  progress_from_total: number;
  progress_to_completed: number;
  progress_to_total: number;
}

interface ReassignmentInfo {
  editor_notes: string | null;
  history: ReassignmentEntry[];
}

interface LogDetail {
  id: string;
  editor_id: string;
  request_id: string | null;
  request_type: string;
  vertical: string | null;
  title: string | null;
  assigned_creatives: number;
  completed_creatives: number;
  status: string;
  started_at: string;
  last_activity_at: string;
  completed_at: string | null;
  date_bucket: string;
  created_at: string;
  editor_name: string;
  editor_display_name: string;
  notes: Note[];
  reassignment_info: ReassignmentInfo | null;
}

interface Props {
  logId: string;
  onClose: () => void;
  onUpdated: () => void;
}

export function TrackingLogDetailModal({ logId, onClose, onUpdated }: Props) {
  const navigate = useNavigate();
  const [log, setLog] = useState<LogDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [noteError, setNoteError] = useState('');
  const [showRequestDetail, setShowRequestDetail] = useState(false);

  const fetchLog = async () => {
    try {
      const res = await trackingReportApi.getLog(logId);
      setLog(res.data.data);
    } catch (err) {
      console.error('Failed to load log detail', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLog(); }, [logId]);

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setAddingNote(true);
    setNoteError('');
    try {
      await trackingReportApi.addNote(logId, { note: newNote.trim() });
      setNewNote('');
      await fetchLog();
      onUpdated();
    } catch (err: any) {
      setNoteError(err.response?.data?.error || 'Failed to add note');
    } finally {
      setAddingNote(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    setUpdatingStatus(true);
    try {
      await trackingReportApi.updateLog(logId, { status: newStatus });
      await fetchLog();
      onUpdated();
    } catch (err) {
      console.error('Failed to update status', err);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const progress = log && log.assigned_creatives > 0
    ? Math.min(Math.round((log.completed_creatives / log.assigned_creatives) * 100), 100)
    : 0;

  const progressBarColor = log
    ? log.status === 'completed' ? 'bg-emerald-500'
      : log.completed_creatives > 0 ? 'bg-amber-400'
      : 'bg-rose-400'
    : 'bg-gray-300';

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-lg w-full max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white truncate pr-4">
            {log?.title || 'Work Log Details'}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded shrink-0">
            <X size={18} />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <RefreshCw className="animate-spin text-blue-600" size={24} />
          </div>
        ) : log ? (
          <>
            {/* Log Info */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 shrink-0 space-y-3">
              {/* Meta badges */}
              <div className="flex flex-wrap gap-2 items-center">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                  log.status === 'completed' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                    : log.status === 'reassigned' ? 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
                    : log.status === 'cancelled' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300'
                    : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300'
                }`}>
                  {log.status === 'completed' ? <CheckCircle2 size={12} /> : log.status === 'reassigned' ? <RefreshCw size={12} /> : log.status === 'cancelled' ? <XCircle size={12} /> : <Clock size={12} />}
                  {log.status}
                </span>
                <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                  log.request_type === 'manual' ? 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                    : 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                }`}>
                  {log.request_type === 'manual' ? 'Manual' : log.request_type === 'launch_request' ? 'Launch Request' : 'File Request'}
                </span>
                {log.vertical && <span className="text-xs text-gray-500 dark:text-gray-400">{log.vertical}</span>}
              </div>

              {/* Details grid */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-xs text-gray-500 dark:text-gray-400 block">Editor</span>
                  <span className="font-medium text-gray-900 dark:text-white">{log.editor_display_name || log.editor_name}</span>
                </div>
                <div>
                  <span className="text-xs text-gray-500 dark:text-gray-400 block">Date</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {new Date((log.date_bucket || '').split('T')[0] + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                </div>
                <div>
                  <span className="text-xs text-gray-500 dark:text-gray-400 block">Started</span>
                  <span className="text-gray-700 dark:text-gray-300">
                    {log.started_at ? new Date(log.started_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                  </span>
                </div>
                <div>
                  <span className="text-xs text-gray-500 dark:text-gray-400 block">Last Activity</span>
                  <span className="text-gray-700 dark:text-gray-300">
                    {log.last_activity_at ? new Date(log.last_activity_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                  </span>
                </div>
              </div>

              {/* Progress bar */}
              {log.request_type !== 'manual' && log.assigned_creatives > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-500 dark:text-gray-400">Creatives: {log.completed_creatives} / {log.assigned_creatives}</span>
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{progress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                    <div className={`h-2 rounded-full transition-all ${progressBarColor}`} style={{ width: `${progress}%` }} />
                  </div>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex flex-wrap gap-2">
                {log.status === 'active' && (
                  <>
                    <button onClick={() => handleStatusChange('completed')} disabled={updatingStatus}
                      className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 transition">
                      <CheckCircle2 size={12} /> Mark Complete
                    </button>
                    {log.request_type === 'manual' && (
                      <button onClick={() => handleStatusChange('cancelled')} disabled={updatingStatus}
                        className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs font-medium rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 transition">
                        <XCircle size={12} /> Cancel
                      </button>
                    )}
                  </>
                )}

                {/* View Request (read-only) + Go to File Request (full actions) */}
                {log.request_id && log.request_type !== 'manual' && (
                  <>
                    <button
                      onClick={() => setShowRequestDetail(true)}
                      className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs font-medium rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition"
                    >
                      <Eye size={12} /> View Request
                    </button>
                    <button
                      onClick={() => { onClose(); navigate(`/file-requests?openRequestId=${log.request_id}`); }}
                      className="flex items-center gap-1 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 text-xs font-medium rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/40 transition"
                    >
                      <ExternalLink size={12} /> Go to File Request
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Reassignment Info */}
            {log.reassignment_info && (log.reassignment_info.editor_notes || log.reassignment_info.history.length > 0) && (
              <div className="p-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2 mb-2">
                  <ArrowRight size={14} /> Assignment Details
                </h3>
                {log.reassignment_info.editor_notes && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-2.5 mb-2">
                    <p className="text-xs font-medium text-blue-700 dark:text-blue-300 mb-0.5">Assignment Note:</p>
                    <p className="text-sm text-blue-800 dark:text-blue-200">{log.reassignment_info.editor_notes}</p>
                  </div>
                )}
                {log.reassignment_info.history.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Reassignment History:</p>
                    {log.reassignment_info.history.map((r, i) => (
                      <div key={i} className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-2.5 text-xs">
                        <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                          <span className="font-medium">{r.from_name}</span>
                          <ArrowRight size={10} />
                          <span className="font-medium">{r.to_name}</span>
                          <span className="ml-auto">{new Date(r.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                        </div>
                        {r.reassignment_note && (
                          <p className="text-gray-700 dark:text-gray-300 mt-1">{r.reassignment_note}</p>
                        )}
                        <div className="text-gray-400 mt-0.5">
                          Progress: {r.progress_from_completed}/{r.progress_from_total} → {r.progress_to_completed}/{r.progress_to_total}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Notes section */}
            <div className="flex-1 overflow-y-auto p-4">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2 mb-3">
                <MessageSquare size={14} /> Notes ({log.notes.length})
              </h3>
              {log.notes.length === 0 ? (
                <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-6">No notes yet. Add one below.</p>
              ) : (
                <div className="space-y-3 mb-4">
                  {log.notes.map(note => (
                    <div key={note.id} className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3">
                      <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{note.note}</p>
                      <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
                        <span className="font-medium">{note.author_name || 'Unknown'}</span>
                        <span>&middot;</span>
                        <span>{new Date(note.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Add note input */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 shrink-0">
              {noteError && (
                <p className="text-xs text-red-600 mb-2">{noteError}</p>
              )}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newNote}
                  onChange={e => setNewNote(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddNote(); } }}
                  placeholder="Add a note..."
                  className="flex-1 px-3 py-2 text-sm border rounded-lg bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-white placeholder-gray-400"
                />
                <button onClick={handleAddNote} disabled={addingNote || !newNote.trim()}
                  className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition">
                  <Send size={16} />
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="p-8 text-center text-gray-500">Work log not found</div>
        )}
      </div>

      {/* Read-only File Request Details overlay */}
      {showRequestDetail && log?.request_id && (
        <FileRequestDetailsModal
          requestId={log.request_id}
          onClose={() => setShowRequestDetail(false)}
          onUpdate={() => {}}
          readOnly
        />
      )}
    </div>
  );
}
